import Vue from 'vue';
import { mapState, mapGetters, mapMutations } from 'vuex';

const COMMENT_MAX_LENGTH = 30;
const MAX_STARS = 5;

export const ResponsesRowMixin = {
  filters: {
    parseSource: function (value) {
      if (!value) return '';
      value = decodeURI(value.toString())
        .replace(/^https?\:\/\//i, '')
        .replace(/^www\./i, '');

      return value;
    }
  },

  data() {
    return {
      commentMode: false,
      comment: '',
      is_focused: false
    };
  },

  computed: {
    ...mapState('company_new', ['rates']),

    ...mapGetters('company_new', ['response_balance']),

    path() {
      return `/responses/${this.model.id}`;
    },

    vacancy_path() {
      return `/vacancies/${this.vacancy.id}`;
    },

    selected: {
      get() {
        return this.$store.getters['responses/isSelected'](this.model);
      },

      set(val) {
        this.$store.commit('responses/select', {
          model: this.model,
          value: val
        });
      }
    },

    status() {
      if (this.not_paid) {
        return this.$tr('responses.list.unpaid');
      }

      return this.model.status.name;
    },

    candidate() {
      return this.model.candidate;
    },

    vacancy() {
      return this.model.vacancy;
    },

    vacancyCreator() {
      if (!this.vacancy || !this.vacancy.user) {
        return '';
      }

      return !this.is_japan_locale
        ? `${this.vacancy.user.first_name} ${this.vacancy.user.last_name}`
        : `${this.vacancy.user.last_name} ${this.vacancy.user.first_name}`;
    },

    source() {
      return this.model.source;
    },

    stars() {
      return this.model.star ? this.model.star.value : 0;
    },

    stars_unselected() {
      return MAX_STARS - this.stars;
    },

    company() {
      return this.$store.getters['user/company'];
    },
    created_at() {
      let date = this.model.created_at;
      return date ? this.$moment(date).format(this.dateFormat) : null;
    },
    created_at_time() {
      let date = this.model.created_at;
      let format = this.company.time_format === 12 ? 'hh:mm a' : 'HH:mm';
      return date ? this.$moment(date, null, 'en').format(format) : null;
    },
    date_sent() {
      let date = this.model.date_sent;
      return date ? this.$moment(date).format(this.dateFormat) : null;
    },

    date_sent_time() {
      let date = this.model.date_sent;
      let format = this.company.time_format === 12 ? 'hh:mm a' : 'HH:mm';
      return date ? this.$moment(date, null, 'en').format(format) : null;
    },

    lastCommentMessage() {
      return this.model.comments.length > 0 ? this.model.comments[0].message : null;
    },

    clippedLastCommentMessage() {
      const comment = this.lastCommentMessage;

      if (comment) {
        return comment.length > COMMENT_MAX_LENGTH ? comment.slice(0, COMMENT_MAX_LENGTH) + '...' : comment;
      }

      return null;
    },

    isNew() {
      return parseInt(this.model.status.id) === 1 || this.not_paid;
    },

    fullNameToDisplay() {
      return !this.is_japan_locale
        ? [this.candidate.name, this.candidate.surname].join(' ')
        : [this.candidate.surname, this.candidate.name].join(' ');
    },

    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    },

    dateFormat() {
      return this.is_japan_locale ? 'YYYY.MM.DD' : 'DD.MM.YYYY';
    },

    save_no_paid_responses() {
      if (!this.rates) {
        return false;
      }

      return this.rates.save_no_paid_responses;
    },

    not_paid() {
      return this.model.is_paid === false || this.model.is_paid === 0;
    },

    is_paid() {
      return this.model.is_paid;
    },

    is_response_disabled() {
      if (
        (this.response_balance || this.response_balance === null || this.is_paid) &&
        this.visibility &&
        !this.refusal
      ) {
        return false;
      }

      return true;
    },

    visibility() {
      return this.model.interview && this.model.interview.visibility ? this.model.interview.visibility.is_active : true;
    },

    hasVideo() {
      if (!this.model || !this.model.questions || !this.model.questions.length) {
        return true;
      }
      return !!this.model.questions.find(item => item.is_video_answer_enabled);
    },

    refusal() {
      return this.model.refusal;
    }
  },

  methods: {
    ...mapMutations('responses', ['updateCommentsForResponse']),

    getCurrentAttribute(header, model) {
      return model.attribute_values.filter(item => `attribute_${item.attribute.code}` === header.id)[0];
    },

    allowSelect() {
      const isSelectionMode = getSelection().toString().length > 0;

      if (!isSelectionMode) {
        this.routeToCard();
      }
    },

    routeToCard() {
      if (this.showRoute) {
        this.$router.push(this.showRoute);
      }
    },

    focus_textarea() {
      this.is_focused = true;
    },

    updateComments(comments) {
      // Разворачиваем комментарии, т.к. при показе в таблице
      // должен отображаться последний по дате комментарий
      const reverseComments = [...comments].reverse();

      this.updateCommentsForResponse({
        responseId: this.model.id,
        comments: reverseComments
      });
    },

    leaveComment(event) {
      const message = event.target.value;

      if (message === this.lastCommentMessage) {
        this.commentMode = false;
        return;
      }

      if (message.length > 0) {
        const lastCommentOnBack = this.model.comments.length > 0 ? this.model.comments[0] : null;

        if (lastCommentOnBack) {
          this.$store
            .dispatch('response/updateComment', {
              responseId: this.model.id,
              commentId: lastCommentOnBack.id,
              message: event.target.value
            })
            .then(comments => this.updateComments(comments));
        } else {
          this.$store
            .dispatch('response/createComment', {
              id: this.model.id,
              message: event.target.value
            })
            .then(comments => {
              this.updateComments(comments);
              if (window.gtag) {
                window.gtag({ event: 'response_comment' });
              }
            });
        }
      }
      this.commentMode = false;
      this.is_focused = false;
    },

    commentKeyPress(event) {
      if (event.code === 'Enter') {
        // to prevent double comment add, use blur instead of direct method leaveComment call
        this.$refs.commentInput.blur();
      }
    },

    editLastComment() {
      if (this.lastCommentMessage) {
        this.comment = this.lastCommentMessage;
      }

      this.commentMode = true;
      Vue.nextTick(() => {
        this.$refs.commentInput.focus();
      });
    }
  }
};
